<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useFunnelReportsStore } from 'dashboard/stores/funnelReports';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import FunnelChart from 'dashboard/components-next/Crm/Reports/FunnelChart.vue';
import PreAttendanceDrilldownDialog from './PreAttendanceDrilldownDialog.vue';

const { t } = useI18n();
const pipelinesStore = usePipelinesStore();
const reportsStore = useFunnelReportsStore();
const store = useStore();

const selectedPipeline = ref(null);
const selectedCampaign = ref(null);
const since = ref('');
const until = ref('');

// Período no padrão da plataforma (mesmo WootDatePicker da tela de Auditoria).
const customDateRange = ref([subDays(new Date(), 29), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_30_DAYS);

const toIsoDate = date => (date ? format(date, 'yyyy-MM-dd') : '');

const report = computed(() => reportsStore.report);

const allCampaigns = computed(
  () => store.getters['campaigns/getAllCampaigns'] || []
);
const campaignOptions = computed(() => [
  { value: null, label: t('REPORT.FUNNEL.ALL_CAMPAIGNS') },
  ...allCampaigns.value.map(c => ({
    value: c.id,
    label: c.title || `#${c.id}`,
  })),
]);

const commonParams = () => ({
  pipeline_id: selectedPipeline.value,
  since: since.value || undefined,
  until: until.value || undefined,
  campaign_id: selectedCampaign.value || undefined,
});

const fetchReport = async () => {
  await reportsStore.fetch(commonParams());
};

const exportCsv = async () => {
  await reportsStore.exportCsv(commonParams());
};

const onDateRangeChangeAuto = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  since.value = toIsoDate(startDate);
  until.value = toIsoDate(endDate);
  fetchReport();
};

onMounted(async () => {
  await Promise.all([
    pipelinesStore.fetchAll(),
    store.dispatch('campaigns/get').catch(() => {}),
  ]);
  if (pipelinesStore.list[0]) {
    selectedPipeline.value = pipelinesStore.list[0].id;
    await fetchReport();
  }
});

function formatCents(v) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format((Number(v) || 0) / 100);
}

// % de um estágio do funil pré-atendimento sobre o total de entrantes.
function preStageRate(count, total) {
  if (!total) return 0;
  return Math.round((Number(count) / Number(total)) * 1000) / 10;
}

const hasData = computed(() => (report.value?.summary?.total ?? 0) > 0);

// Total de entrantes serve como denominador das barras e do empty state.
const preEntrantesTotal = computed(
  () => report.value?.pre_attendance?.stages?.[0]?.count ?? 0
);
const hasPreData = computed(() => preEntrantesTotal.value > 0);

// Options para SelectInput - mantém label/value do <select> nativo.
const pipelineOptions = computed(() =>
  pipelinesStore.list.map(p => ({ value: p.id, label: p.name }))
);

// Estados de tela (presentation only): controlam loading/empty global.
const hasPipeline = computed(() => pipelinesStore.list.length > 0);
// Loading cobre a primeira carga (pipelines ou relatório) sem dados em tela.
const isInitialLoading = computed(
  () => (pipelinesStore.loading || reportsStore.loading) && !report.value
);
// Empty global: já carregou, há pipeline, mas o relatório não trouxe dados.
const showGlobalEmpty = computed(
  () =>
    !reportsStore.loading &&
    hasPipeline.value &&
    !!report.value &&
    !hasData.value &&
    !hasPreData.value
);

// SelectInput não expõe @change - reage via watch (substitui o `@change="fetchReport"`).
watch(selectedPipeline, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) fetchReport();
});
watch(selectedCampaign, (newVal, oldVal) => {
  if (newVal !== oldVal) fetchReport();
});

// Drill-down
const drillOpen = ref(false);
const drillStage = ref(null);
const drillLabel = ref('');

async function openDrill(stage) {
  drillStage.value = stage.key;
  drillLabel.value = stage.label;
  drillOpen.value = true;
  await reportsStore.fetchDrill({
    ...commonParams(),
    stage: stage.key,
    page: 1,
  });
}

async function changeDrillPage(page) {
  await reportsStore.fetchDrill({
    ...commonParams(),
    stage: drillStage.value,
    page,
  });
}

function closeDrill() {
  drillOpen.value = false;
  reportsStore.resetDrill();
}
</script>

<template>
  <!-- Wrapper padrão da plataforma (igual Visão geral / Pipeline) -->
  <div class="w-full h-full overflow-y-auto bg-n-surface-1 font-inter">
    <div class="max-w-[88rem] mx-auto px-6 py-6 flex flex-col gap-4">
      <!-- Header -->
      <header class="flex items-center justify-between gap-4">
        <div class="min-w-0">
          <h1
            class="text-heading-1 text-n-slate-12 min-h-10 flex items-center mb-0"
          >
            {{ $t('REPORT.FUNNEL.TITLE') }}
          </h1>
          <p class="text-body-main text-n-slate-11 mb-0 mt-1 max-w-3xl">
            {{ $t('REPORT.FUNNEL.SUBTITLE') }}
          </p>
        </div>
        <div class="flex-shrink-0">
          <NextButton
            icon="i-lucide-download"
            size="sm"
            :is-loading="reportsStore.loading"
            :label="$t('REPORT.FUNNEL.EXPORT_CSV')"
            @click="exportCsv"
          />
        </div>
      </header>

      <!-- Card de filtros: período + campanha + pipeline (padrão CSAT) -->
      <section
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-4 flex items-center gap-3 flex-wrap"
      >
        <!-- DÍVIDA: migrar date picker para primitivo premium de components-next (ver plano 03). -->
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          show-range
          @date-range-changed="onDateRangeChangeAuto"
        />
        <div class="flex items-center gap-2 ml-auto">
          <label class="text-body-small text-n-slate-11">{{ $t('REPORT.FUNNEL.FILTERS.CAMPAIGN') }}</label>
          <div class="min-w-[12rem]">
            <SelectInput
              v-model="selectedCampaign"
              :options="campaignOptions"
            />
          </div>
          <label class="text-body-small text-n-slate-11">{{ $t('REPORT.FUNNEL.FILTERS.PIPELINE') }}</label>
          <div class="min-w-[12rem]">
            <SelectInput
              v-model="selectedPipeline"
              :options="pipelineOptions"
            />
          </div>
        </div>
      </section>

      <!-- Estado: carga inicial (skeleton premium) -->
      <section
        v-if="isInitialLoading"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-16 flex flex-col items-center justify-center text-center gap-3"
      >
        <span
          class="size-14 rounded-2xl bg-n-alpha-2 flex items-center justify-center"
        >
          <span
            class="size-7 rounded-full border-2 border-n-weak border-t-n-brand animate-spin"
          />
        </span>
        <p class="text-heading-3 text-n-slate-12 mb-0">
          {{ $t('REPORT.FUNNEL.LOADING_TITLE') }}
        </p>
        <p class="text-body-main text-n-slate-11 mb-0 max-w-md">
          {{ $t('REPORT.FUNNEL.LOADING_SUBTITLE') }}
        </p>
        <!-- Skeleton de cards para reforçar a percepção de carregamento -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 w-full mt-4">
          <div
            v-for="n in 4"
            :key="n"
            class="h-20 rounded-xl bg-n-alpha-1 animate-pulse"
          />
        </div>
      </section>

      <!-- Estado: nenhum pipeline cadastrado -->
      <section
        v-else-if="!hasPipeline"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-16 flex flex-col items-center justify-center text-center gap-3"
      >
        <span
          class="size-14 rounded-2xl bg-n-alpha-2 flex items-center justify-center"
        >
          <i class="i-lucide-git-branch-plus size-7 text-n-slate-11" />
        </span>
        <p class="text-heading-3 text-n-slate-12 mb-0">
          {{ $t('REPORT.FUNNEL.NO_PIPELINE_TITLE') }}
        </p>
        <p class="text-body-main text-n-slate-11 mb-0 max-w-md">
          {{ $t('REPORT.FUNNEL.NO_PIPELINE_SUBTITLE') }}
        </p>
      </section>

      <!-- Estado: relatório vazio no período -->
      <section
        v-else-if="showGlobalEmpty"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-16 flex flex-col items-center justify-center text-center gap-3"
      >
        <span
          class="size-14 rounded-2xl bg-n-alpha-2 flex items-center justify-center"
        >
          <i class="i-lucide-search-x size-7 text-n-slate-11" />
        </span>
        <p class="text-heading-3 text-n-slate-12 mb-0">
          {{ $t('REPORT.FUNNEL.EMPTY_TITLE') }}
        </p>
        <p class="text-body-main text-n-slate-11 mb-0 max-w-md">
          {{ $t('REPORT.FUNNEL.EMPTY_SUBTITLE') }}
        </p>
      </section>

      <!-- Funil PRÉ-atendimento: campanha → IA → handoff → CRM -->
      <section
        v-if="report?.pre_attendance && !isInitialLoading"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5"
      >
        <header class="flex items-center gap-2 mb-4">
          <i class="i-lucide-filter size-4 text-n-brand" />
          <h2 class="text-heading-3 text-n-slate-12 mb-0">
            {{ $t('REPORT.FUNNEL.SECTIONS.PRE_ATTENDANCE') }}
          </h2>
          <span class="text-body-small text-n-slate-11 ml-2">
            {{ $t('REPORT.FUNNEL.SECTIONS.PRE_ATTENDANCE_HINT') }}
          </span>
        </header>

        <!-- Empty state quando não há conversas entrantes no período -->
        <div
          v-if="!hasPreData"
          class="py-10 flex flex-col items-center justify-center text-center gap-2"
        >
          <span
            class="size-12 rounded-2xl bg-n-alpha-2 flex items-center justify-center"
          >
            <i class="i-lucide-inbox size-6 text-n-slate-11" />
          </span>
          <p class="text-body-main font-medium text-n-slate-12 mb-0">
            {{ $t('REPORT.FUNNEL.PRE_EMPTY_TITLE') }}
          </p>
          <p class="text-body-small text-n-slate-11 mb-0 max-w-md">
            {{ $t('REPORT.FUNNEL.PRE_EMPTY_SUBTITLE') }}
          </p>
        </div>

        <template v-else>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button
              v-for="(s, i) in report.pre_attendance.stages"
              :key="s.key"
              type="button"
              class="rounded-xl bg-n-alpha-1 px-4 py-3 text-left transition hover:bg-n-alpha-2 hover:ring-1 hover:ring-n-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              @click="openDrill(s)"
            >
              <p
                class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
              >
                {{ s.label }}
              </p>
              <p class="text-heading-2 text-n-slate-12 mb-0">{{ s.count }}</p>
              <p v-if="i > 0" class="text-body-small text-n-slate-10 mb-1">
                {{ $t('REPORT.FUNNEL.PRE_STAGE.OF_ENTRANTS', { pct: preStageRate(s.count, preEntrantesTotal) }) }}
              </p>
              <p v-else class="text-body-small text-n-slate-10 mb-1">
                {{ $t('REPORT.FUNNEL.PRE_STAGE.IN_PERIOD') }}
              </p>
              <!-- Barra de progresso proporcional ao total de entrantes -->
              <div
                class="h-1.5 w-full rounded-full bg-n-alpha-2 overflow-hidden"
                role="progressbar"
                :aria-valuenow="preStageRate(s.count, preEntrantesTotal)"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div
                  class="h-full rounded-full transition-[width] duration-500"
                  :class="{
                    'bg-n-brand': i === 0 || i === 3,
                    'bg-n-grass-9': i === 1,
                    'bg-n-amber-9': i === 2,
                  }"
                  :style="{
                    width: `${preStageRate(s.count, preEntrantesTotal)}%`,
                  }"
                />
              </div>
            </button>
          </div>

          <div
            class="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-n-weak"
          >
            <div>
              <p class="text-label-small text-n-slate-11 mb-0.5">
                {{ $t('REPORT.FUNNEL.PRE_CONVERSION.ENTRANTS_TO_AI') }}
              </p>
              <p class="text-body-main font-medium text-n-slate-12 mb-0">
                {{ report.pre_attendance.conversion.entrantes_to_ia }}%
              </p>
            </div>
            <div>
              <p class="text-label-small text-n-slate-11 mb-0.5">
                {{ $t('REPORT.FUNNEL.PRE_CONVERSION.AI_RESOLVED') }}
              </p>
              <p class="text-body-main font-medium text-n-grass-11 mb-0">
                {{ report.pre_attendance.conversion.ia_resolved }}%
              </p>
            </div>
            <div>
              <p class="text-label-small text-n-slate-11 mb-0.5">
                {{ $t('REPORT.FUNNEL.PRE_CONVERSION.HANDOFF') }}
              </p>
              <p class="text-body-main font-medium text-n-amber-11 mb-0">
                {{ report.pre_attendance.conversion.ia_to_handoff }}%
              </p>
            </div>
            <div>
              <p class="text-label-small text-n-slate-11 mb-0.5">
                {{ $t('REPORT.FUNNEL.PRE_CONVERSION.ENTRANTS_TO_LEAD') }}
              </p>
              <p class="text-body-main font-medium text-n-brand mb-0">
                {{ report.pre_attendance.conversion.entrantes_to_lead }}%
              </p>
            </div>
          </div>
        </template>
      </section>

      <PreAttendanceDrilldownDialog
        v-if="drillOpen"
        :stage-label="drillLabel"
        :drill="reportsStore.drill"
        @close="closeDrill"
        @change-page="changeDrillPage"
      />

      <!-- KPIs no padrão CRM em números -->
      <section
        v-if="report"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5"
      >
        <header class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <i class="i-lucide-target size-4 text-n-brand" />
            <h2 class="text-heading-3 text-n-slate-12 mb-0">
              {{ $t('REPORT.FUNNEL.SECTIONS.INDICATORS') }}
            </h2>
          </div>
        </header>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.KPI.TOTAL') }}
            </p>
            <p class="text-heading-2 text-n-slate-12 mb-0">
              {{ report.summary.total }}
            </p>
            <p class="text-body-small text-n-slate-10 mb-0">{{ $t('REPORT.FUNNEL.KPI.TOTAL_HINT') }}</p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.KPI.WON') }}
            </p>
            <p class="text-heading-2 text-n-grass-11 mb-0">
              {{ report.summary.won }}
            </p>
            <p class="text-body-small text-n-slate-10 mb-0">
              {{ formatCents(report.summary.won_amount_cents) }}
            </p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.KPI.AVG_TICKET') }}
            </p>
            <p class="text-heading-2 text-n-slate-12 mb-0">
              {{ formatCents(report.summary.avg_ticket_cents) }}
            </p>
            <p class="text-body-small text-n-slate-10 mb-0">{{ $t('REPORT.FUNNEL.KPI.AVG_TICKET_HINT') }}</p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.KPI.CONVERSION') }}
            </p>
            <p class="text-heading-2 text-n-slate-12 mb-0">
              {{ report.summary.conversion_rate }}%
            </p>
            <p class="text-body-small text-n-slate-10 mb-0">
              {{ $t('REPORT.FUNNEL.KPI.CONVERSION_HINT') }}
            </p>
          </div>
        </div>
      </section>

      <!-- Funil visual -->
      <section
        v-if="report"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5"
      >
        <header class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <i class="i-lucide-funnel size-4 text-n-slate-11" />
            <h2 class="text-heading-3 text-n-slate-12 mb-0">
              {{ $t('REPORT.FUNNEL.SECTIONS.BY_STAGE') }}
            </h2>
          </div>
        </header>

        <div v-if="hasData">
          <FunnelChart
            :stages="report.count_by_stage || []"
            :conversion="report.conversion || []"
          />
        </div>
        <div
          v-else
          class="py-12 flex flex-col items-center justify-center text-center gap-2"
        >
          <span
            class="size-12 rounded-2xl bg-n-alpha-2 flex items-center justify-center"
          >
            <i class="i-lucide-funnel size-6 text-n-slate-11" />
          </span>
          <p class="text-body-main font-medium text-n-slate-12 mb-0">
            {{ $t('REPORT.FUNNEL.BY_STAGE_EMPTY_TITLE') }}
          </p>
          <p class="text-body-small text-n-slate-11 mb-0 max-w-md">
            {{ $t('REPORT.FUNNEL.BY_STAGE_EMPTY_SUBTITLE') }}
          </p>
        </div>
      </section>

      <!-- Conversion entre stages -->
      <section
        v-if="report?.conversion?.length"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5"
      >
        <header class="flex items-center gap-2 mb-4">
          <i class="i-lucide-trending-up size-4 text-n-slate-11" />
          <h2 class="text-heading-3 text-n-slate-12 mb-0">
            {{ $t('REPORT.FUNNEL.SECTIONS.STAGE_CONVERSION') }}
          </h2>
        </header>
        <div class="overflow-hidden rounded-xl border border-n-weak">
          <table class="w-full text-body-main">
            <thead
              class="bg-n-alpha-1 text-xs text-n-slate-11 uppercase tracking-wide"
            >
              <tr class="border-b border-n-weak">
                <th class="text-left px-3 py-2 font-medium">{{ $t('REPORT.FUNNEL.TABLE.FROM') }}</th>
                <th class="text-left px-3 py-2 font-medium">{{ $t('REPORT.FUNNEL.TABLE.TO') }}</th>
                <th class="text-right px-3 py-2 font-medium">{{ $t('REPORT.FUNNEL.TABLE.RATE') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(c, i) in report.conversion"
                :key="i"
                class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors"
              >
                <td class="px-3 py-2 text-n-slate-12">{{ c.from }}</td>
                <td class="px-3 py-2 text-n-slate-12">{{ c.to }}</td>
                <td class="px-3 py-2 text-right font-semibold text-n-slate-12">
                  {{ c.rate }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Top performers -->
      <section
        v-if="report?.top_performers?.length"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5"
      >
        <header class="flex items-center gap-2 mb-4">
          <i class="i-lucide-trophy size-4 text-n-slate-11" />
          <h2 class="text-heading-3 text-n-slate-12 mb-0">{{ $t('REPORT.FUNNEL.SECTIONS.TOP_PERFORMERS') }}</h2>
        </header>
        <div class="overflow-hidden rounded-xl border border-n-weak">
          <table class="w-full text-body-main">
            <thead
              class="bg-n-alpha-1 text-xs text-n-slate-11 uppercase tracking-wide"
            >
              <tr class="border-b border-n-weak">
                <th class="text-left px-3 py-2 font-medium">{{ $t('REPORT.FUNNEL.TABLE.AGENT') }}</th>
                <th class="text-right px-3 py-2 font-medium">{{ $t('REPORT.FUNNEL.TABLE.WON') }}</th>
                <th class="text-right px-3 py-2 font-medium">{{ $t('REPORT.FUNNEL.TABLE.AMOUNT') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="t in report.top_performers"
                :key="t.owner_id"
                class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors"
              >
                <td class="px-3 py-2 text-n-slate-12">{{ t.owner_name }}</td>
                <td class="px-3 py-2 text-right text-n-slate-12">
                  {{ t.won_count }}
                </td>
                <td class="px-3 py-2 text-right font-semibold text-n-grass-11">
                  {{ formatCents(t.amount_cents) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Lost reasons -->
      <section
        v-if="report?.lost_reasons?.length"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5"
      >
        <header class="flex items-center gap-2 mb-4">
          <i class="i-lucide-x-circle size-4 text-n-slate-11" />
          <h2 class="text-heading-3 text-n-slate-12 mb-0">{{ $t('REPORT.FUNNEL.SECTIONS.LOST_REASONS') }}</h2>
        </header>
        <ul
          class="flex flex-col overflow-hidden rounded-xl border border-n-weak"
        >
          <li
            v-for="r in report.lost_reasons"
            :key="r.lost_reason_id"
            class="flex items-center justify-between px-3 py-2 border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors"
          >
            <span class="text-body-main text-n-slate-12">{{ r.name }}</span>
            <span class="text-body-main font-semibold text-n-slate-12">{{
              r.count
            }}</span>
          </li>
        </ul>
      </section>

      <!-- Leads stale -->
      <section
        v-if="report?.stale_leads?.length"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5"
      >
        <header class="flex items-center gap-2 mb-4">
          <i class="i-lucide-alert-triangle size-4 text-n-amber-11" />
          <h2 class="text-heading-3 text-n-slate-12 mb-0">{{ $t('REPORT.FUNNEL.SECTIONS.STALE_LEADS') }}</h2>
        </header>
        <div class="overflow-hidden rounded-xl border border-n-weak">
          <table class="w-full text-body-main">
            <thead
              class="bg-n-alpha-1 text-xs text-n-slate-11 uppercase tracking-wide"
            >
              <tr class="border-b border-n-weak">
                <th class="text-left px-3 py-2 font-medium">{{ $t('REPORT.FUNNEL.TABLE.CONTACT') }}</th>
                <th class="text-left px-3 py-2 font-medium">{{ $t('REPORT.FUNNEL.TABLE.STAGE') }}</th>
                <th class="text-left px-3 py-2 font-medium">{{ $t('REPORT.FUNNEL.TABLE.AGENT') }}</th>
                <th class="text-right px-3 py-2 font-medium">{{ $t('REPORT.FUNNEL.TABLE.DAYS') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="l in report.stale_leads"
                :key="l.id"
                class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors"
              >
                <td class="px-3 py-2 text-n-slate-12">{{ l.contact }}</td>
                <td class="px-3 py-2 text-n-slate-11">{{ l.stage }}</td>
                <td class="px-3 py-2 text-n-slate-11">{{ l.owner }}</td>
                <td class="px-3 py-2 text-right font-semibold text-n-amber-11">
                  {{ l.days_idle }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Métricas IA + Follow-up no padrão CRM em números -->
      <section
        v-if="report?.ai_metrics || report?.follow_up_metrics"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5"
      >
        <header class="flex items-center gap-2 mb-4">
          <i class="i-lucide-sparkles size-4 text-n-brand" />
          <h2 class="text-heading-3 text-n-slate-12 mb-0">
            {{ $t('REPORT.FUNNEL.SECTIONS.AI_FOLLOWUP') }}
          </h2>
        </header>

        <div
          v-if="report?.ai_metrics"
          class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3"
        >
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.AI.CREATED') }}
            </p>
            <p class="text-heading-2 text-n-slate-12 mb-0">
              {{ report.ai_metrics.ai_created_pct }}%
            </p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.AI.QUALIFIED') }}
            </p>
            <p class="text-heading-2 text-n-slate-12 mb-0">
              {{ report.ai_metrics.ai_qualified_pct }}%
            </p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.AI.AVG_CONFIDENCE') }}
            </p>
            <p class="text-heading-2 text-n-slate-12 mb-0">
              {{ report.ai_metrics.avg_confidence }}
            </p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.AI.TOTAL_LEADS') }}
            </p>
            <p class="text-heading-2 text-n-slate-12 mb-0">
              {{ report.ai_metrics.total_leads }}
            </p>
          </div>
        </div>

        <div
          v-if="report?.follow_up_metrics"
          class="grid grid-cols-2 md:grid-cols-4 gap-3"
        >
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.FOLLOWUP.TEMPLATES_APPLIED') }}
            </p>
            <p class="text-heading-2 text-n-slate-12 mb-0">
              {{ report.follow_up_metrics.applied_total }}
            </p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.FOLLOWUP.BY_AI') }}
            </p>
            <p class="text-heading-2 text-n-brand mb-0">
              {{ report.follow_up_metrics.applied_by_ai_pct }}%
            </p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.FOLLOWUP.ACTIVITIES_DONE') }}
            </p>
            <p class="text-heading-2 text-n-grass-11 mb-0">
              {{ report.follow_up_metrics.activities_completed }}
            </p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
            <p
              class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
            >
              {{ $t('REPORT.FUNNEL.FOLLOWUP.ACTIVITIES_PENDING') }}
            </p>
            <p class="text-heading-2 text-n-amber-11 mb-0">
              {{ report.follow_up_metrics.activities_pending }}
            </p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
